import { capitalizeFirst } from '@utils/helpers';
import { countByKind, kindColors } from '../components/overlay/kinds';
import { allOf, anyOne, countTitle, isReindeer, plural } from '../components/overlay/subjectWords';
import { CALF_RATIO, rateBins, snapToGrid } from '../components/overlay/tracksModel';
import { MODE_LABEL } from '../components/overlay/useCountModel';
import { countSteps, correctionRows, pageRows } from './reportCorrections';
import { heroFrame, MOMENTS_FROM_SEC, selectMoments } from './reportMoments';
import {
	clock,
	fileStamp,
	reportDate,
	runDate,
	sizeRangeText,
	stripUnit,
	videoLabel,
} from './reportText';

/**
 * Everything the video count report shows, from the results page at the moment of the download (design:
 * .task/browser-report/plan.html). Every number, chart and frame comes from the tracks the page keeps (useCountModel:
 * Sensitivity, the corrections, Size), so the PDF count is the page count. Words and layout follow the backend report
 * (video_detection_report.py, branch feat/reindeer-find).
 */

/** Page 3 "Corrections": in the report when there are corrections. Off: never (the numbers still include them). */
export const REPORT_CORRECTIONS_PAGE = true;

// models that output a track once it has been tracked for 1 s (backend TRACKER_MODELS)
const TRACKER_MODELS = ['BFM', 'RFDETR', 'BFM-RFDETR', 'SEARCH'];
// "Size kept" rows the left panel has room for; more kinds with a range share one row
const SIZE_ROWS = 3;

/** Relative body sizes on the backend report's fixed bins (video_tracks.size_histogram); calf-sized below CALF_RATIO. */
export const sizeBins = (sizes, bins = 22, lo = 0.35, hi = 1.75) => {
	const counts = new Array(bins).fill(0);
	const width = (hi - lo) / bins;
	sizes.forEach(v => {
		counts[Math.min(bins - 1, Math.max(0, Math.floor((v - lo) / width)))] += 1;
	});
	return { counts, lo, hi, calf: sizes.filter(v => v < CALF_RATIO).length };
};

const point = p => `(${Math.round(p[0])}, ${Math.round(p[1])})`;

// "Size kept" rows: each kind with a range (the size filter's groups that have one) and how many it leaves out; no
// range at all is "every size"
const sizeRows = (ranged, unit, name) => {
	if (!ranged.length) return [['Size kept', 'every size']];
	if (ranged.length > SIZE_ROWS) {
		const hidden = ranged.reduce((a, g) => a + g.hidden, 0);
		return [['Size kept', `${ranged.length} kinds with a range · ${hidden} left out`]];
	}
	return ranged.map(g => [
		g.kind ? `Size kept · ${name(g.kind)}` : 'Size kept',
		`${sizeRangeText(g.range, unit)} · ${g.hidden} left out`,
	]);
};

/** The frames the report reads from the video: page 1's hero, then page 2's moments (each once, in time order). */
export const reportFrames = count => {
	const { index } = count;
	return {
		hero: heroFrame(index),
		moments: selectMoments(index.inView, index.fps),
	};
};

/**
 * snapshot: { at (Date of the download), projectId, projectName, videoName, runAt (Date or null), modelId,
 * modelLabel, subject, category, layer (the corrections), count (useCountModel's result) }.
 * options.correctionsPage: page 3 on or off; options.text: what makes a person's text printable (reportText.pdfText).
 */
export const buildReport = (
	snapshot,
	{ correctionsPage = REPORT_CORRECTIONS_PAGE, text = s => s ?? '' } = {}
) => {
	const { at, count, layer } = snapshot;
	const { index, mode, stages } = count;
	const doc = stages.doc;
	const fps = index.fps;
	const n = index.nFrames;
	// where the analysed clip starts in the source video: only the Analysed fact says it; every other time is clip
	// time, as the player and Your changes show it
	const offset = Number(doc.time_offset_sec) || 0;
	const duration = n / fps;
	const timeOf = f => f / fps;
	const line = mode === 'line' ? count.line : null;

	// what was counted, in the page's words: "reindeer", "cars and trucks"; animals are words of a reindeer job only
	const subject = text(snapshot.subject);
	const named = text(snapshot.category) || subject;
	const reindeer = isReindeer(subject);
	const many = allOf(named);
	const one = anyOne(named);
	const [animals, animal] = reindeer ? ['animals', 'animal'] : [many, one];
	const tracker = TRACKER_MODELS.includes(String(snapshot.modelId || '').toUpperCase());

	const kept = index.tracks;
	const counted = kept.length;
	const most = index.maxInView;
	const colors = kindColors(count.kinds || []);
	const byKind = countByKind(kept, count.kinds || []).map(({ kind, n: k }) => ({
		words: `${k} ${k === 1 ? text(kind) : plural(text(kind))}`,
		color: colors[kind].hex,
	}));

	// the page holds each frame the general search read for its step
	const held = Array.from({ length: n }, (_, f) => index.inView[snapToGrid(index.grid, f)]);
	const series = mode === 'max' ? held : Array.from(count.series || []);
	const soFar = { hover: 'counted', line: 'passed', max: 'in view' }[mode];

	const sizes = kept.map(tr => tr.rel_size).filter(Number.isFinite);
	const hist = sizeBins(sizes);

	const words = {
		hover: `${many} counted`,
		line: `${many} passed the line`,
		max: `${many} in view at once, the most`,
	}[mode];
	const detail = {
		hover: `each ${animal} once${tracker ? ', after it is tracked for 1 s' : ''}`,
		line: `${count.events ?? 0} crossing events, both directions`,
		max: `at ${clock(timeOf(index.maxInViewFrame))}`,
	}[mode];
	const calfValue = { value: `${hist.calf} of ${sizes.length}`, words: 'calf-sized' };
	const countedValue = { value: String(counted), words: `${many} counted` };
	const mostValue = { value: String(most), words: 'most in view at once' };
	let second;
	if (mode === 'line') second = [countedValue, mostValue];
	else if (mode === 'max') second = reindeer ? [countedValue, calfValue] : [countedValue];
	else second = reindeer ? [mostValue, calfValue] : [mostValue];

	const sensitivity = `${Math.round(stages.sensitivity * 100)} %`;
	const unit = count.sizeFilter?.unit;
	const ranged = (count.sizeFilter?.groups || []).filter(g => g.range);
	const settings = [['Method', MODE_LABEL[mode]]];
	if (line) settings.push(['Line', `${point(line.start)} → ${point(line.end)} px`]);
	settings.push(['Sensitivity', sensitivity]);
	settings.push(...sizeRows(ranged, unit, text));
	settings.push(['Model', snapshot.modelLabel || 'Unknown model']);
	if (tracker) settings.push(['Rule', 'counted after 1 s tracked']);

	const strip = rateBins(series, fps, mode !== 'max');
	const frames = reportFrames(count);
	const hero = frames.hero;
	const fromStart =
		frames.moments.length > 0 && frames.moments[0] >= Math.round(MOMENTS_FROM_SEC * fps);
	const momentSoFar = mode === 'line' ? series : index.uniqueSeries;
	const kindWords = byKind.length ? ' in the colour of its kind' : '';

	const changes = count.changes || { added: 0, removed: 0, merged: 0 };
	const corrected = changes.added + changes.removed + changes.merged > 0;
	let corrections = null;
	if (correctionsPage && corrected) {
		const rows = correctionRows(layer, doc, {
			subject: one,
			fps,
			follow: layer?.follow,
		}).map(r => ({ ...r, change: text(r.change), how: text(r.how) }));
		corrections = {
			pills: [
				changes.removed > 0 && { kind: 'remove', words: `${changes.removed} removed` },
				changes.added > 0 && { kind: 'add', words: `${changes.added} added` },
				changes.merged > 0 && { kind: 'merge', words: `${changes.merged} merged` },
			].filter(Boolean),
			steps: countSteps({
				mode,
				line,
				stages,
				index,
				total: count.total,
				sensitivityLabel: `Sensitivity ${sensitivity}`,
				sizeLabel:
					ranged.length === 1 ? `Size ${sizeRangeText(ranged[0].range, unit)}` : 'Size',
			}),
			pages: pageRows(rows),
		};
	}

	const projectName = text(snapshot.projectName) || 'Video project';
	return {
		title: `Video count report · ${projectName}`,
		fileName: `video_count_report_${fileStamp(at)}`,
		date: `Report of ${reportDate(at)}`,
		projectName,
		projectId: snapshot.projectId,
		videoName: text(snapshot.videoName),
		facts: [
			['Video', videoLabel(doc.height, fps)],
			['Frames', String(n)],
			['Analysed', `${clock(offset)}–${clock(offset + duration)}`],
			[
				'Run',
				Number.isNaN(snapshot.runAt?.getTime()) || !snapshot.runAt
					? '—'
					: runDate(snapshot.runAt),
			],
		],
		settings,
		modelLabel: snapshot.modelLabel || 'Unknown model',
		reindeer,
		// height to width of the video frames
		frameRatio: doc.height / doc.width,
		count: {
			title: countTitle(named),
			big: count.total,
			words,
			detail,
			byKind,
			second,
		},
		hero: {
			frame: hero,
			caption: `${clock(timeOf(hero))}  ·  ${index.inView[hero]} in view, the most at once  ·  dots: counted ${many}${line ? '  ·  dashed: counting line' : ''}`,
		},
		strip: {
			label: `${capitalizeFirst(animals)} ${{ hover: 'counted', line: 'passing', max: 'in view, the most' }[mode]} per ${stripUnit(strip.barSec)}`,
			bins: strip.bins,
		},
		sizeTitle: reindeer ? 'Body size of counted animals' : 'Size of what was counted',
		sizes: counted
			? {
					...hist,
					legend: reindeer
						? [
								{ calf: true, words: `${hist.calf} calf-sized` },
								{ words: `${sizes.length - hist.calf} adult-sized` },
							]
						: [{ words: `${counted} counted` }],
					note: reindeer
						? 'Size relative to the herd at the same distance (estimate)'
						: 'Size relative to others at the same distance (estimate)',
				}
			: null,
		noneCounted: `No ${animals} counted`,
		series: {
			values: series,
			inView: mode === 'max' ? null : held,
			words,
			caption:
				mode === 'max'
					? `most in view: ${most} at ${clock(timeOf(index.maxInViewFrame))}`
					: `0 → ${series[n - 1] ?? 0} ${soFar} in ${clock(duration)}`,
		},
		moments: {
			description: `Frames with ${animals} in view${fromStart ? ` from ${clock(MOMENTS_FROM_SEC, 0)}` : ''}, evenly spaced, with the frame of the most in view. Each dot is a counted ${one}${kindWords}; the short trail is its last second.`,
			frames: frames.moments.map(f => ({
				frame: f,
				time: clock(timeOf(f)),
				caption: `${held[f]} in view  ·  ${momentSoFar[f]} ${mode === 'line' ? 'passed' : 'counted'} so far`,
			})),
		},
		duration,
		corrections,
		pages: 2 + (corrections ? corrections.pages.length : 0),
	};
};
